import styled, { css } from 'styled-components';
import { alpha } from '@mui/material/styles';
import theme from '../../Theme';
import {
  DASHBOARD_CONTENT_SHELL_RADIUS,
} from '../../utils/navigation/dashboardSidebarLayout';
import { TooltipWrapper as BaseTooltipWrapper, Tooltip as BaseTooltip } from './tooltipStyles';
import { SITE_CHROME_BAR_HEIGHT_PX, SITE_CHROME_CONTROL_SIZE_PX } from './HeaderStyles';

const navbarActionButtonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${SITE_CHROME_CONTROL_SIZE_PX}px;
  height: ${SITE_CHROME_CONTROL_SIZE_PX}px;
  padding: 0;
  line-height: 0;
  border-radius: 8px;
  border: none;
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
  cursor: pointer;
  outline: none;
  font-family: 'Poppins', 'Arial', sans-serif;

  .navbar-action-btn-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    line-height: 0;
  }
`;

export const NavbarWrapper = styled.nav`
  background-color: ${theme.palette.background.default};
  border-bottom: 1px solid ${theme.palette.grey[200]};
  padding: 0;
  position: sticky;
  top: 0;
  width: 100%;
  z-index: 1000;
  height: ${SITE_CHROME_BAR_HEIGHT_PX}px;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  overflow: visible;
  flex-shrink: 0;

  @media (min-width: 1040px) {
    top: 0;
    border-top-left-radius: ${DASHBOARD_CONTENT_SHELL_RADIUS};
    border-top-right-radius: ${DASHBOARD_CONTENT_SHELL_RADIUS};
  }
`;

export const NavbarContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 100%;
  margin: 0;
  /* Match DashboardPageShell horizontal gutters (theme spacing 3 / 2). */
  padding: 0 24px;
  width: 100%;
  height: 100%;
  gap: 12px;
  box-sizing: border-box;
  overflow: visible;

  @media (max-width: 600px) {
    padding: 0 16px;
  }
`;

export const NavbarLeftSection = styled.div`
  display: flex;
  align-items: center;
  height: auto;
  gap: 10px;
  min-width: 0;
  flex: 0 0 auto;
  overflow: visible;

  /* Desktop sidebar toggle renders null — drop the empty flex item so search
     left-aligns with page content (e.g. Overview greeting). */
  &:empty {
    display: none;
  }
`;

/** Flex slot for the dashboard omnibar search pill. */
export const NavbarSearchSlot = styled.div`
  display: flex;
  align-items: center;
  height: auto;
  flex: 0 0 auto;
  min-width: 0;
  margin: 0;
  overflow: visible;

  @media (max-width: 768px) {
    display: none;
  }
`;
export const NavbarProfile = styled.div`
  display: flex;
  align-items: center;
  height: auto;
  position: relative;
  margin-left: auto;
  margin-right: 0;
  flex-shrink: 0;
  gap: 8px;
  overflow: visible;
`;

export const NavbarActionsDivider = styled.div`
  width: 1px;
  height: 24px;
  background-color: ${theme.palette.border.subtle};
  flex-shrink: 0;
`;

export const NavbarActions = styled.div`
  display: flex;
  align-items: center;
  height: auto;
  gap: 2px;
  flex-shrink: 0;
`;

export const NotificationButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${SITE_CHROME_CONTROL_SIZE_PX}px;
  height: ${SITE_CHROME_CONTROL_SIZE_PX}px;
  min-width: ${SITE_CHROME_CONTROL_SIZE_PX}px;
  max-width: ${SITE_CHROME_CONTROL_SIZE_PX}px;
  flex: 0 0 auto;
  overflow: visible;
  container-type: size;
  border-radius: 8px;
  border: none;
  box-sizing: border-box;
  color: ${theme.palette.accent.main};
  background: transparent;
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
  cursor: pointer;
  position: relative;
  padding: 0;
  outline: none;
  flex-shrink: 0;
  line-height: 0;

  svg {
    width: 20px;
    height: 20px;
    display: block;
    flex-shrink: 0;
  }

  svg path {
    stroke: currentColor;
  }

  .notification-badge {
    position: absolute;
    top: 4cqh;
    right: 4cqw;
    z-index: 1;
    background-color: ${theme.palette.accent.main};
    color: ${theme.palette.primary.contrastText};
    border-radius: 999px;
    min-width: max(14px, 38cqmin);
    height: max(14px, 38cqmin);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: max(9px, 22cqmin);
    font-weight: 600;
    padding: 0 max(2px, 8cqmin);
    border: max(1px, 4.5cqmin) solid ${theme.palette.background.default};
    box-shadow: 0 2px 4px ${alpha(theme.palette.text.primary, 0.12)};
  }

  &:hover {
    background-color: ${theme.palette.accent.light};
    color: ${theme.palette.accent.dark};
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

export const NavbarSecondaryButton = styled.button`
  ${navbarActionButtonBase}
  color: ${({ $accent }) => ($accent ? theme.palette.accent.main : theme.palette.text.primary)};
  background: transparent;

  .navbar-action-btn-icon svg {
    width: 20px;
    height: 20px;
    stroke: currentColor;
    fill: none;
  }

  & > svg {
    width: 20px;
    height: 20px;
    fill: currentColor;
  }

  &:hover {
    background: ${({ $accent }) => ($accent ? theme.palette.accent.light : theme.palette.applied.light)};
    color: ${({ $accent }) => ($accent ? theme.palette.accent.dark : theme.palette.text.primary)};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const ProfileIcon = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${SITE_CHROME_CONTROL_SIZE_PX}px;
  height: ${SITE_CHROME_CONTROL_SIZE_PX}px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid transparent;
  outline: none;
  box-shadow: none;
  background: transparent;
  flex-shrink: 0;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: ${theme.palette.accent.main};
  }

  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: none;
  }

  img,
  .profile-picture {
    width: ${SITE_CHROME_CONTROL_SIZE_PX}px;
    height: ${SITE_CHROME_CONTROL_SIZE_PX}px;
    object-fit: contain;
    border-radius: 50%;
    background-color: transparent;
    border: none;
  }

  .profile-icon-svg {
    width: ${SITE_CHROME_CONTROL_SIZE_PX}px;
    height: ${SITE_CHROME_CONTROL_SIZE_PX}px;
    fill: ${theme.palette.text.primary};
    cursor: pointer;
  }
`;

export const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background-color: ${theme.palette.background.paper};
  border-radius: 12px;
  padding: 8px 0;
  min-width: 240px;
  z-index: 1002;
  border: 1px solid ${theme.palette.border.subtle};
  box-shadow:
    0 1px 2px ${alpha(theme.palette.text.primary, 0.04)},
    0 8px 20px ${alpha(theme.palette.text.primary, 0.12)};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  transform: ${({ $visible }) => ($visible ? 'translateY(0) scale(1)' : 'translateY(-8px) scale(0.95)')};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: top right;
  overflow: hidden;
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
`;

export const DropdownList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const DropdownDivider = styled.li`
  height: 1px;
  margin: 6px 8px;
  background-color: ${theme.palette.border.subtle};
  pointer-events: none;
`;

export const DropdownItem = styled.li`
  padding: 8px 12px;
  margin: 2px 8px;
  border-radius: 8px;
  transition: background-color 0.2s ease, color 0.2s ease;

  &:hover {
    background-color: ${theme.palette.background.softHover};
  }

  &.active {
    background-color: ${theme.palette.background.soft};
    color: ${theme.palette.accent.main};
  }

  &.active:hover {
    background-color: ${theme.palette.background.softHover};
  }

  &:first-of-type {
    margin-top: 4px;
  }

  &:last-child {
    margin-bottom: 4px;
  }

  display: flex;
  align-items: center;
  color: ${theme.palette.text.primary};
  text-decoration: none;
  width: calc(100% - 16px);
  font-size: var(--font-body-sm);
  font-weight: 500;
  cursor: pointer;

  .dropdown-icon {
    margin-right: 10px;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: ${theme.palette.text.primary};
    transition: color 0.2s ease, opacity 0.15s ease;

    svg {
      width: 18px;
      height: 18px;
      stroke-width: 1.75px;
    }
  }

  &.active .dropdown-icon {
    color: ${theme.palette.accent.main};
    opacity: 1;
  }
`;

export const TooltipWrapper = BaseTooltipWrapper;
export const Tooltip = styled(BaseTooltip).attrs({
  $zIndex: 1001,
  $elevated: true,
})``;
